import React, {
  useState,
  forwardRef,
  useMemo,
  useRef,
  useEffect,
  useCallback,
  useLayoutEffect,
} from 'react';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import styles from './MultiSelectDropDown.module.scss';
import AppIcon from '../AppIcon/AppIcon';
import Title from '../TitleStyles/title';
import Input from '../Input/Input';
import { icons } from '@/school-management/utils/icons';
import { autocompleteService } from '@/school-management/services/autocomplete';
import {
  TeacherAutocompleteItem,
  StudentAutocompleteItem,
  GroupAutocompleteItem,
  ClassCodeAutocompleteItem,
  AutocompleteResponse,
} from '@/school-management/types/response/autocomplete';
import type { CascadingFilterRequest } from '@/school-management/types/response/autocomplete';

type AutocompleteItem =
  | TeacherAutocompleteItem
  | StudentAutocompleteItem
  | GroupAutocompleteItem
  | ClassCodeAutocompleteItem;
type AutocompleteType = 'teachers' | 'students' | 'groups' | 'grades' | 'class_code';

const MIN_AUTOCOMPLETE_SEARCH_QUERY = 1;
const EMPTY_EXCLUDE_VALUES: string[] = [];

function SelectedItemLabel({ label }: { label: string }) {
  const textRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const check = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth + 1);
    };

    check();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', check);
      return () => window.removeEventListener('resize', check);
    }

    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [label]);

  return (
    <span className={styles.selectedItemTextWrapper}>
      <span ref={textRef} className={styles.selectedItemText}>
        {label}
      </span>
      {isTruncated && (
        <span className={styles.selectedItemTooltip} role='tooltip'>
          {label}
        </span>
      )}
    </span>
  );
}

export const autocompleteItemToMultiSelectOption = (
  item: AutocompleteItem,
  autocompleteType: AutocompleteType
): MultiSelectOption => {
  switch (autocompleteType) {
    case 'teachers':
      const t = item as TeacherAutocompleteItem;
      return {
        label: `${t.firstName} ${t.lastName}`,
        value: t.id,
        data: {
          email: t.email,
          item: item,
          autocompleteType: autocompleteType,
        },
      };
    case 'students':
      const s = item as StudentAutocompleteItem;
      return {
        label: `${s.firstName} ${s.lastName}`,
        value: s.id,
        data: {
          email: s.email,
          username: s.username,
          item: item,
          autocompleteType: autocompleteType,
        },
      };
    case 'groups':
      const g = item as GroupAutocompleteItem;
      return {
        label: g.groupName,
        value: g.groupId,
        data: {
          teachers: g.teachers,
          item: item,
          autocompleteType: autocompleteType,
        },
      };
    case 'class_code':
      const classCode = item as ClassCodeAutocompleteItem;
      return {
        label: classCode,
        value: classCode,
        data: {
          classCode: classCode,
          item: item,
          autocompleteType: autocompleteType,
        },
      };
    default:
      return { label: '', value: '', data: { item, autocompleteType } };
  }
};

export interface MultiSelectOption {
  label: string;
  value: string;
  picture?: keyof typeof icons;
  data?: any;
}

interface MultiSelectDropDownProps {
  options: MultiSelectOption[];
  Open?: boolean;
  title?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  label?: string;
  value?: MultiSelectOption[];
  name?: string;
  onChange?: (values: MultiSelectOption[]) => void;
  onBlur?: () => void;
  inputRef?: React.Ref<any>;
  staticTitle?: string;
  style?: React.CSSProperties;
  isEmptyBorder?: boolean;
  isSeparate?: boolean;
  withHeader?: boolean;
  maxHeight?: string;
  showSelectAll?: boolean;
  maxDisplayItems?: number;
  showAllSelected?: boolean;
  maxSelections?: number;
  variant?: 'default' | 'filter';
  containerClassName?: string;
  headerClassName?: string;
  listClassName?: string;
  arrowClassName?: string;
  itemClassName?: string;
  searchContainerClassName?: string;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  enableAutocomplete?: boolean;
  organizationId?: string;
  autocompleteType?: AutocompleteType;
  autocompleteStartOptions?: MultiSelectOption[];
  autoScrollIntoView?: boolean;
  cascadingFilters?: CascadingFilterRequest;
  excludeValues?: string[];
  isLoadingOptions?: boolean;
}

const MultiSelectDropDown = forwardRef<HTMLDivElement, MultiSelectDropDownProps>(
  (
    {
      options,
      Open,
      title,
      placeholder,
      searchPlaceholder,
      label,
      value = [],
      name,
      onChange,
      onBlur,
      inputRef,
      staticTitle,
      style,
      isEmptyBorder,
      isSeparate,
      withHeader = false,
      maxHeight = '300px',
      showSelectAll = true,
      maxDisplayItems = 2,
      showAllSelected = false,
      maxSelections,
      variant = 'default',
      containerClassName,
      headerClassName,
      listClassName,
      arrowClassName,
      itemClassName,
      searchContainerClassName,
      onLoadMore,
      hasMore = false,
      isLoadingMore = false,
      enableAutocomplete = false,
      organizationId,
      autocompleteType = 'teachers',
      autocompleteStartOptions = [],
      autoScrollIntoView,
      cascadingFilters,
      excludeValues = EMPTY_EXCLUDE_VALUES,
      isLoadingOptions = false,
    },
    ref
  ) => {
    const { t } = useAdminTranslation();
    const excludedValueSet = useMemo(() => new Set(excludeValues), [excludeValues]);
    const [isOpen, setIsOpen] = useState(Open || false);
    const [searchQuery, setSearchQuery] = useState('');
    const [autocompleteOptions, setAutocompleteOptions] = useState<MultiSelectOption[]>([]);
    const [isLoadingAutocomplete, setIsLoadingAutocomplete] = useState(false);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const autocompleteRequestIdRef = useRef(0);

    const toggleDropdown = () => setIsOpen(!isOpen);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (isOpen) {
        const handleOutsideClick = (event: any) => {
          if (containerRef.current && !containerRef.current.contains(event.target)) {
            setIsOpen(false);
          }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('touchstart', handleOutsideClick);

        return () => {
          document.removeEventListener('mousedown', handleOutsideClick);
          document.removeEventListener('touchstart', handleOutsideClick);
        };
      }
    }, [isOpen]);

    useEffect(() => {
      if (isOpen && autoScrollIntoView) {
        const timer = setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            });
          }
        }, 300);

        return () => {
          clearTimeout(timer);
        };
      }
    }, [isOpen, autoScrollIntoView]);

    const fetchAutocomplete = useCallback(
      async (searchTerm: string) => {
        if (
          !enableAutocomplete ||
          !organizationId ||
          searchTerm.trim().length < MIN_AUTOCOMPLETE_SEARCH_QUERY
        ) {
          setAutocompleteOptions([]);
          return;
        }

        const requestId = ++autocompleteRequestIdRef.current;

        setIsLoadingAutocomplete(true);
        try {
          let response: AutocompleteResponse<AutocompleteItem>;
          switch (autocompleteType) {
            case 'teachers':
              response = await autocompleteService.getTeachers(
                organizationId,
                searchTerm,
                cascadingFilters
              );
              break;
            case 'students':
              response = await autocompleteService.getStudents(
                organizationId,
                searchTerm,
                cascadingFilters
              );
              break;
            case 'groups':
              response = await autocompleteService.getGroups(
                organizationId,
                searchTerm,
                cascadingFilters
              );
              break;
            case 'class_code':
              response = await autocompleteService.getClassCodes(
                organizationId,
                searchTerm,
                cascadingFilters
              );
              break;
            default:
              return;
          }

          if (requestId !== autocompleteRequestIdRef.current) {
            return;
          }

          if (response.status !== 'success' || !response.data) {
            setAutocompleteOptions([]);
            return;
          }

          const toOption = (item: AutocompleteItem): MultiSelectOption => {
            return autocompleteItemToMultiSelectOption(item, autocompleteType);
          };

          setAutocompleteOptions(response.data.map(toOption).filter(option => option.value));
        } catch (error) {
          if (requestId !== autocompleteRequestIdRef.current) {
            return;
          }
          console.error('Autocomplete error:', error);
          setAutocompleteOptions([]);
        } finally {
          if (requestId === autocompleteRequestIdRef.current) {
            setIsLoadingAutocomplete(false);
          }
        }
      },
      [enableAutocomplete, organizationId, autocompleteType, cascadingFilters]
    );

    const fetchAutocompleteRef = useRef(fetchAutocomplete);
    useEffect(() => {
      fetchAutocompleteRef.current = fetchAutocomplete;
    }, [fetchAutocomplete]);

    const cascadingFiltersKey = useMemo(
      () => JSON.stringify(cascadingFilters ?? null),
      [cascadingFilters]
    );

    useEffect(() => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (enableAutocomplete && searchQuery.trim().length >= MIN_AUTOCOMPLETE_SEARCH_QUERY) {
        debounceTimerRef.current = setTimeout(() => {
          fetchAutocompleteRef.current(searchQuery);
        }, 300);
      } else {
        setAutocompleteOptions([]);
      }

      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, [searchQuery, enableAutocomplete, organizationId, autocompleteType, cascadingFiltersKey]);

    const originalIndexMap = useMemo(() => {
      const map = new Map<string, number>();
      options.forEach((opt, idx) => map.set(opt.value, idx));
      return map;
    }, [options]);

    const filteredOptions = useMemo(() => {
      const mergeBrowseOptions = (...sources: MultiSelectOption[][]) => {
        const seen = new Set<string>();
        const merged: MultiSelectOption[] = [];

        sources.flat().forEach(option => {
          if (
            !option.value ||
            !option.label ||
            seen.has(option.value) ||
            excludedValueSet.has(option.value)
          ) {
            return;
          }
          seen.add(option.value);
          merged.push(option);
        });

        return merged;
      };

      const base = (() => {
        if (enableAutocomplete) {
          const setOptionsFromAutocomplete =
            searchQuery.trim().length >= MIN_AUTOCOMPLETE_SEARCH_QUERY;

          const selectedValues = value || [];

          if (setOptionsFromAutocomplete) {
            if (autocompleteOptions.length === 0) {
              return selectedValues;
            }

            const seen = new Set(selectedValues.map(o => o.value));
            const fromAutocomplete = autocompleteOptions.filter(opt => {
              if (
                !opt.value ||
                !opt.label ||
                seen.has(opt.value) ||
                excludedValueSet.has(opt.value)
              ) {
                return false;
              }
              seen.add(opt.value);
              return true;
            });
            return [...selectedValues, ...fromAutocomplete];
          }

          const browseOptions = mergeBrowseOptions(autocompleteStartOptions, options);
          if (browseOptions.length === 0) {
            return selectedValues;
          }

          const seen = new Set(selectedValues.map(o => o.value));
          const fromBrowse = browseOptions.filter(opt => {
            if (seen.has(opt.value)) {
              return false;
            }
            seen.add(opt.value);
            return true;
          });
          return [...selectedValues, ...fromBrowse];
        }

        if (!searchQuery) return options.filter(option => !excludedValueSet.has(option.value));

        const q = searchQuery.toLowerCase();
        return options.filter(option => {
          if (excludedValueSet.has(option.value)) {
            return false;
          }
          const labelMatch = option.label.toLowerCase().includes(q);
          const email = (option as any)?.data?.email as string | undefined;
          const emailMatch = typeof email === 'string' && email.toLowerCase().includes(q);
          return labelMatch || emailMatch;
        });
      })();

      const selectedIndexMap = new Map<string, number>(
        (value || []).map((v, idx) => [v.value, idx])
      );

      return [...base].sort((a, b) => {
        const aSel = selectedIndexMap.has(a.value);
        const bSel = selectedIndexMap.has(b.value);
        if (aSel && !bSel) return -1;
        if (!aSel && bSel) return 1;
        if (aSel && bSel) {
          return (selectedIndexMap.get(b.value) ?? 0) - (selectedIndexMap.get(a.value) ?? 0);
        }
        return (originalIndexMap.get(a.value) ?? 0) - (originalIndexMap.get(b.value) ?? 0);
      });
    }, [
      options,
      searchQuery,
      value,
      originalIndexMap,
      autocompleteOptions,
      enableAutocomplete,
      isLoadingAutocomplete,
      autocompleteStartOptions,
      excludedValueSet,
    ]);

    const handleOptionClick = (option: MultiSelectOption) => {
      if (!onChange) return;

      const isSelected = value.some(v => v.value === option.value);
      let newValue: MultiSelectOption[];

      if (isSelected) {
        newValue = value.filter(v => v.value !== option.value);
      } else {
        newValue = [...value, option];
      }

      onChange(newValue);
    };

    const handleSelectAll = () => {
      if (!onChange) return;

      const allSelected = filteredOptions.every(option =>
        value.some(v => v.value === option.value)
      );

      if (allSelected) {
        const newValue = value.filter(v => !filteredOptions.some(fo => fo.value === v.value));
        onChange(newValue);
      } else {
        const newOptions = filteredOptions.filter(
          option => !value.some(v => v.value === option.value)
        );
        onChange([...value, ...newOptions]);
      }
    };

    const handleClearAll = () => {
      if (onChange) {
        onChange([]);
      }
    };

    const finalPlaceholder = placeholder || t('Select options...');
    const finalSearchPlaceholder = searchPlaceholder || t('Search options...');

    const handleRemoveItem = (itemToRemove: MultiSelectOption, event: React.MouseEvent) => {
      event.stopPropagation();
      if (!onChange) return;

      const newValue = value.filter(v => v.value !== itemToRemove.value);
      onChange(newValue);
    };

    const getDisplayContent = () => {
      if (variant === 'filter') {
        if (value.length === 0) {
          return (
            <span style={{ color: '#94A3B8', fontWeight: 400, opacity: 0.5 }}>
              {finalPlaceholder}
            </span>
          );
        }
        return (
          <span style={{ color: '#08263f', fontWeight: 600 }}>
            {value.length} {t('multiSelectDropDown.selected', 'selected')}
          </span>
        );
      }

      if (value.length === 0) {
        return <span style={{ color: '#08263F', fontWeight: 400 }}>{finalPlaceholder}</span>;
      }

      return (
        <div className={styles.selectedItemsContainer}>
          <div className={styles.selectedItems}>
            {(showAllSelected ? value : value.slice(0, maxDisplayItems))
              .filter((item, index, self) => self.findIndex(i => i.value === item.value) === index)
              .map((item, index) => (
                <span key={`${item.value}-${index}`} className={styles.selectedItemTag}>
                  <SelectedItemLabel label={item.label} />
                  <button
                    type='button'
                    className={styles.removeItemButton}
                    onClick={e => handleRemoveItem(item, e)}
                    aria-label={`Remove ${item.label}`}
                  >
                    <AppIcon icon='x' size={12} />
                  </button>
                </span>
              ))}
          </div>

          <button
            type='button'
            className={styles.deleteAllButton}
            onClick={e => {
              e.stopPropagation();
              handleClearAll();
            }}
            aria-label='Remove all selected items'
            title={t('multiSelectDropDown.removeAll') || ''}
          >
            {t('multiSelectDropDown.removeAll')}
          </button>
        </div>
      );
    };

    const allFilteredSelected =
      filteredOptions.length > 0 &&
      filteredOptions.every(option => value.some(v => v.value === option.value));

    return (
      <div className={styles.inputContainer} style={style} ref={containerRef}>
        {label && <Title subTitle={label} style={{ marginTop: withHeader ? '32px' : '' }} />}
        <div
          className={`${styles.dropdownContainer} ${isOpen ? styles.open : ''} ${
            variant === 'filter' ? styles.filterVariant : ''
          } ${containerClassName || ''}`}
          ref={ref}
          onBlur={onBlur}
        >
          <div
            className={`${styles.dropdownHeader} ${isOpen ? styles.open : ''} ${
              options[0]?.picture ? styles.picture : ''
            } ${isEmptyBorder ? styles.emptyBorder : ''} ${headerClassName || ''}`}
            onClick={toggleDropdown}
          >
            {options[0]?.picture && (
              <span>
                <AppIcon icon='portuguese' size={24} />
              </span>
            )}
            <span className={styles.label}>
              {staticTitle && (
                <span style={{ color: '#94A3B8', fontWeight: 400 }}>{staticTitle}</span>
              )}
              <div
                style={{
                  color: '#08263f',
                  fontWeight: 400,
                  marginLeft: staticTitle ? 4 : 0,
                  width: '100%',
                  opacity: value.length === 0 ? 0.5 : 1,
                }}
              >
                {getDisplayContent()}
              </div>
            </span>
            <AppIcon
              icon='dropDown'
              className={`${styles.dropdownArrow} ${isOpen ? styles.open : ''} ${
                arrowClassName || ''
              }`}
              size={16}
            />
          </div>
          {isOpen && (
            <div
              className={`${styles.dropdownList} ${isEmptyBorder ? styles.emptyBorder : ''} ${
                isSeparate ? styles.separate : ''
              } ${listClassName || ''}`}
              style={{ maxHeight }}
            >
              <div className={`${styles.searchContainer} ${searchContainerClassName || ''}`}>
                <Input
                  type='search'
                  placeholder={finalSearchPlaceholder || ''}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className={`${styles.searchInput} ${
                    variant === 'filter' ? styles.filterSearchInput : ''
                  }`}
                />
              </div>

              <ul className={styles.optionsList}>
                {isLoadingAutocomplete || isLoadingOptions ? (
                  <li className={styles.noResults}>{t('multiSelectDropDown.loading', '')}</li>
                ) : enableAutocomplete &&
                  filteredOptions.length === 0 &&
                  (searchQuery || '').length < MIN_AUTOCOMPLETE_SEARCH_QUERY ? (
                  <li className={styles.noResults}>{t('multiSelectDropDown.typeText', '')}</li>
                ) : filteredOptions.length === 0 ? (
                  <li className={styles.noResults}>{t('multiSelectDropDown.noResults', '')}</li>
                ) : (
                  filteredOptions.map(option => {
                    const isSelected = value.some(v => v.value === option.value);
                    const limitReached =
                      typeof maxSelections === 'number' && value.length >= maxSelections;
                    const isDisabled = limitReached && !isSelected;
                    return (
                      <li
                        key={option.value}
                        className={`${styles.dropdownItem} ${isSelected ? styles.selected : ''} ${
                          isDisabled ? styles.disabled : ''
                        } ${itemClassName || ''}`}
                        onClick={() => {
                          if (!isDisabled) handleOptionClick(option);
                        }}
                      >
                        <div className={styles.checkbox}>
                          <input
                            type='checkbox'
                            checked={isSelected}
                            onChange={() => {}}
                            className={styles.checkboxInput}
                            disabled={isDisabled}
                          />
                          {isSelected ? (
                            <AppIcon icon='dropDownBlueV' size={22} />
                          ) : (
                            <span className={styles.checkboxCustom}></span>
                          )}
                        </div>

                        {option?.picture != null && (
                          <span>
                            <AppIcon icon='portuguese' size={24} />
                          </span>
                        )}

                        <div className={styles.optionTextBlock}>
                          <span className={styles.optionText}>{option.label}</span>
                          <span className={styles.optionSubText}>{option.data.email}</span>
                        </div>
                      </li>
                    );
                  })
                )}
              </ul>
              {hasMore &&
                onLoadMore &&
                (!enableAutocomplete ||
                  searchQuery.trim().length < MIN_AUTOCOMPLETE_SEARCH_QUERY) && (
                  <div className={styles.loadMoreContainer}>
                    <button
                      type='button'
                      className={styles.loadMoreButton}
                      onClick={e => {
                        e.stopPropagation();
                        onLoadMore();
                      }}
                      disabled={isLoadingMore}
                    >
                      {isLoadingMore
                        ? t('multiSelectDropDown.loading', '')
                        : t('multiSelectDropDown.loadMore', '')}
                    </button>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
    );
  }
);

MultiSelectDropDown.displayName = 'MultiSelectDropDown';

export default MultiSelectDropDown;
